import { DeliveryMethod } from '@/dealers/_gen/gql'
import useUnloadConfirmation from '@/dealers/hooks/useUnloadConfirmation'
import useConfig from '@/gf/hooks/useConfig'
import Id from '@/gf/modules/Id'
import TimeM from '@/gf/modules/Time'
import { Address, Maybe } from '@/types'
import { yupResolver } from '@/gf/lib/yupResolver'
import isUndefined from 'lodash/isUndefined'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import { DeepPartial, useForm } from 'react-hook-form'
import * as Yup from 'yup'

export enum Urgency {
  HIGH = 3,
  MEDIUM = 2,
  LOW = 1,
}

export type FormValues = {
  customerContactId: Maybe<string>
  taxCost: Maybe<number>
  shippingCost: Maybe<number>
  customerDiscount: number | null
  quoteNumber: Maybe<string>
  deliveryMethod: DeliveryMethod
  items: {
    id: Maybe<string>
    partNumber: string
    altPartNumbers: string[]
    description: string
    unitPrice: Maybe<number>
    quantity: number
    inStock: boolean
    externalId: Maybe<string>
    taskNumber: Maybe<string>
    suggestion: Maybe<boolean>
    availabilityDate: Maybe<string>
    rfqPartId: string | null
    rfqPartNumber: string | null
  }[]
  pickupAddress: Maybe<Address>
  shippingAddress: Maybe<Address>
  urgency: Maybe<Urgency>
  neededByDate: Maybe<DateTime>
  comments: Maybe<string>
}

export const DIRECT_QUOTE_KEY = 'new'

const quoteFormName = (uniqueKey: string) => `sh-quote-form-v3-${uniqueKey}`

export const persistForm = (uniqueKey: string, values: DeepPartial<FormValues>) => {
  const json = JSON.stringify(values)
  localStorage.setItem(quoteFormName(uniqueKey), json)
}

export const deletePersistedForm = (uniqueKey: string) =>
  localStorage.removeItem(quoteFormName(uniqueKey))

const loadForm = (uniqueKey: string, initialValues: FormValues) => {
  const json = localStorage.getItem(quoteFormName(uniqueKey))

  if (!json) return initialValues

  const persistedValues = JSON.parse(json) as DeepPartial<FormValues>

  return {
    customerContactId: isUndefined(persistedValues.customerContactId)
      ? initialValues.customerContactId
      : persistedValues.customerContactId,
    shippingCost: isUndefined(persistedValues.shippingCost)
      ? initialValues.shippingCost
      : persistedValues.shippingCost,
    taxCost: isUndefined(persistedValues.taxCost) ? initialValues.taxCost : persistedValues.taxCost,
    customerDiscount: isUndefined(persistedValues.customerDiscount)
      ? initialValues.customerDiscount
      : persistedValues.customerDiscount,
    quoteNumber: isUndefined(persistedValues.quoteNumber)
      ? initialValues.quoteNumber
      : persistedValues.quoteNumber,
    deliveryMethod: isUndefined(persistedValues.deliveryMethod)
      ? initialValues.deliveryMethod
      : persistedValues.deliveryMethod,
    items: !Array.isArray(persistedValues.items)
      ? initialValues.items
      : // Older persisted forms predate `altPartNumbers`; defensively normalize
        // each item's alts to `string[]` so the type contract stays honest
        // after hydration. Mirrors the extractor sanitize in
        // `extractionToLineItems` so both write paths converge on the same shape.
        (persistedValues.items as FormValues['items']).map((item) => ({
          ...item,
          altPartNumbers: Array.isArray(item?.altPartNumbers)
            ? Array.from(
                new Set(
                  item.altPartNumbers
                    .filter((s): s is string => typeof s === 'string')
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0)
                )
              )
            : [],
        })),
    pickupAddress: isUndefined(persistedValues.pickupAddress)
      ? initialValues.pickupAddress
      : (persistedValues.pickupAddress as FormValues['pickupAddress']),
    shippingAddress: isUndefined(persistedValues.shippingAddress)
      ? initialValues.shippingAddress
      : (persistedValues.shippingAddress as FormValues['shippingAddress']),
    urgency: isUndefined(persistedValues.urgency) ? initialValues.urgency : persistedValues.urgency,
    neededByDate: isUndefined(persistedValues.neededByDate)
      ? initialValues.neededByDate
      : DateTime.fromISO(persistedValues.neededByDate as unknown as string),
    comments: isUndefined(persistedValues.comments)
      ? initialValues.comments
      : persistedValues.comments,
  }
}

// The customer's address is what the quote ships, taxes and prints against, and
// Sales Hub has no other screen that can set it — so a quote must not be
// submittable without one. `Yup.object().required()` is not enough: it passes on
// any non-null object, including the all-empty shape a stale localStorage cache
// or a partial customer record hands back (GEA-6577). Check the fields.
export const CUSTOMER_ADDRESS_REQUIRED = "Add the customer's address to continue"

const REQUIRED_ADDRESS_FIELDS = ['lineOne', 'city', 'state', 'postalCode', 'country'] as const

export const isCompleteAddress = (value: unknown): boolean =>
  !!value &&
  typeof value === 'object' &&
  REQUIRED_ADDRESS_FIELDS.every((field) => {
    const fieldValue = (value as Record<string, unknown>)[field]
    return typeof fieldValue === 'string' && fieldValue.trim() !== ''
  })

const validationSchema = (isDirectQuote: boolean, requireCustomerAddress: boolean) =>
  Yup.object().shape({
    customerContactId: isDirectQuote
      ? Yup.string().nullable().required('Customer is required')
      : Yup.string().nullable(),
    taxCost: Yup.number().nullable(),
    customerDiscount: Yup.number().nullable(),
    shippingCost: Yup.number().when('deliveryMethod', ([deliveryMethod], schema) =>
      deliveryMethod === DeliveryMethod.Pickup
        ? schema.nullable().optional()
        : schema.nullable().required('Shipping Cost is required')
    ),
    quoteNumber: Yup.string().nullable(),
    deliveryMethod: Yup.string()
      .oneOf([DeliveryMethod.Pickup, DeliveryMethod.Shipping, DeliveryMethod.VendorDelivery])
      .required('Delivery method is required'),
    items: Yup.array(
      Yup.object().shape({
        partNumber: Yup.string().trim().required('Part Number is required'),
        description: Yup.string()
          .trim()
          .max(255)
          .required('Part Description is required')
          .label('Part Description'),
        externalId: Yup.string().nullable(),
        taskNumber: Yup.string().nullable(),
        suggestion: Yup.boolean().nullable(),
        unitPrice: Yup.number()
          .nullable()
          .min(0, 'Price cannot be negative')
          .required('Price is required'),
        quantity: Yup.number()
          .required('Quantity is required')
          .integer('Quantity must be a whole number')
          .min(1, 'Quantity must be at least 1'),
        inStock: Yup.boolean().required(),
        availabilityDate: Yup.date().when('inStock', {
          is: false,
          then: (schema) =>
            schema
              .nullable()
              .required('required field')
              .min(TimeM.toDateInputFormat(TimeM.now()), 'Cannot be earlier than today'),
          otherwise: (schema) => schema.nullable(),
        }),
      })
    ).min(1),
    pickupAddress: Yup.object().nullable().optional(),
    // Only require it where the field is actually on screen. When quoting an
    // inbound RFQ the delivery section is not rendered and the address comes
    // from the request itself — requiring it there would block the quote behind
    // a field error with no field to fix.
    shippingAddress: requireCustomerAddress
      ? Yup.object()
          .nullable()
          .test('complete-address', CUSTOMER_ADDRESS_REQUIRED, isCompleteAddress)
      : Yup.object().when('deliveryMethod', ([deliveryMethod], schema) =>
          deliveryMethod === DeliveryMethod.Pickup
            ? schema.nullable().optional()
            : schema.nullable().required('Please add the shipping address')
        ),
    urgency: isDirectQuote
      ? Yup.number()
          .oneOf([Urgency.HIGH, Urgency.MEDIUM, Urgency.LOW])
          .nullable()
          .required('Select the urgency')
      : Yup.number().nullable().optional(),
    neededByDate: isDirectQuote
      ? Yup.string().nullable().required('Selected the date the needs the parts')
      : Yup.string().nullable().optional(),
    comments: Yup.string().nullable().optional(),
  })

const useQuoteForm = ({
  rfqId,
  storeOrderId,
  initialValues,
  onTouched,
  canSetDeliveryMethod = true,
}: {
  rfqId: string | undefined
  storeOrderId: string | undefined
  initialValues: FormValues
  onTouched: (() => void) | undefined
  canSetDeliveryMethod?: boolean
}) => {
  const config = useConfig()
  const [touched, setTouched] = useState(false)

  const storageUniqueKey = useMemo(
    () =>
      rfqId || storeOrderId ? Id.shorten((rfqId ?? storeOrderId) as string) : DIRECT_QUOTE_KEY,
    [rfqId, storeOrderId]
  )

  const isDirectQuote = !storeOrderId && !rfqId

  // Mirrors `showDeliveryMethod` in QuoteBuilder — the condition under which the
  // delivery/address section is rendered, and so the only condition under which
  // the customer address is a field the seller can fix.
  const showDeliveryMethod = Boolean((isDirectQuote || storeOrderId) && canSetDeliveryMethod)

  const { control, handleSubmit, register, formState, getValues, setValue, watch } =
    useForm<FormValues>({
      shouldFocusError: true,
      shouldUnregister: false,
      // reValidateMode: 'onChange',
      resolver: yupResolver(validationSchema(isDirectQuote, showDeliveryMethod)),
      defaultValues: loadForm(storageUniqueKey, initialValues),
    })

  useEffect(() => {
    const subscription = watch(
      (data: DeepPartial<FormValues>) => persistForm(storageUniqueKey, data),
      initialValues
    )

    return () => subscription.unsubscribe()
  }, [watch])

  useEffect(() => {
    if (formState.isDirty && !touched) {
      setTouched(true)
      if (onTouched) onTouched()
    }
  }, [formState.isDirty])

  // TODO: use a proper env variable
  useUnloadConfirmation(formState.isDirty && config.gfBaseUrl.indexOf('local.') === -1)

  return { control, handleSubmit, register, formState, getValues, setValue, storageUniqueKey }
}

export default useQuoteForm
